import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { config } from '../config';
const PEOPLE_API_URL = config.api.people;
/**
 * Get authorization headers with JWT token from auth store
 */
function getAuthHeaders() {
    const { tokens, user } = useAuthStore.getState();
    const token = tokens?.idToken;
    const tenantId = user?.tenantId || 'tenant-001';
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
    };
}
/**
 * Handle API errors
 */
async function handleResponse(response) {
    if (!response.ok) {
        // For 404s, return a more specific error that can be detected by retry logic
        if (response.status === 404) {
            throw new Error(`HTTP 404: ${response.url}`);
        }
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
}
/**
 * Fetch the authenticated user's employee profile and manager details.
 * Self-service endpoint - no admin role required.
 */
export async function getMyProfile() {
    const response = await fetch(`${PEOPLE_API_URL}/me`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Hook to fetch the authenticated user's profile with TanStack Query.
 * Automatically caches and refetches on window focus.
 */
export function useMyProfile() {
    return useQuery({
        queryKey: ['my-profile'],
        queryFn: getMyProfile,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        refetchOnWindowFocus: false, // Don't refetch when window regains focus
        refetchOnMount: false, // Don't refetch on component mount if data exists
        retry: (failureCount, error) => {
            // Don't retry on 404 (user profile doesn't exist yet)
            if (error instanceof Error && error.message.includes('404')) {
                return false;
            }
            return failureCount < 1; // Retry other errors once
        },
    });
}
/**
 * Fetch list of employees.
 * For managers: automatically filtered to direct reports by backend.
 * For admins: returns all employees.
 */
export async function listEmployees() {
    const response = await fetch(`${PEOPLE_API_URL}/employees`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Hook to fetch direct reports (for managers) or all employees (for admins).
 * Backend automatically filters based on role.
 */
export function useDirectReports() {
    return useQuery({
        queryKey: ['direct-reports'],
        queryFn: listEmployees,
        staleTime: 2 * 60 * 1000, // 2 minutes
    });
}
